import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Loader2, Database, RefreshCw, FileArchive, AlertTriangle,
    Download, DownloadCloud, ChevronLeft, ChevronRight, GitMerge,
    Settings2, History, Columns3, Square, Play,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import MappingEditor from './MappingEditor';
import { MovedToListManagement } from './ImportDiffParts';
import RunHistory from './RunHistory';
import PitchBookDataBrowser from './PitchBookDataBrowser';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILES_PAGE_SIZE = 10;

function fmtBytes(n) {
    if (!n) return '—';
    if (n > 1e6) return `${(n / 1e6).toFixed(1)} MB`;
    if (n > 1e3) return `${(n / 1e3).toFixed(0)} KB`;
    return `${n} B`;
}

const STATUS_STATES = {
    processed: {
        label: 'Ready',
        help: 'Indexed and available',
        cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
        dot: 'bg-emerald-500',
    },
    new: {
        label: 'Waiting',
        help: 'Will index automatically',
        cls: 'bg-blue-50 text-blue-700 ring-blue-100',
        dot: 'bg-blue-500',
    },
    indexing: {
        label: 'Indexing…',
        help: 'Running in the background',
        cls: 'bg-violet-50 text-violet-700 ring-violet-100',
        dot: 'bg-violet-500 animate-pulse',
    },
    failed: {
        label: 'Failed',
        help: 'Indexing did not finish — retry',
        cls: 'bg-red-50 text-red-700 ring-red-100',
        dot: 'bg-red-500',
    },
    cancelled: {
        label: 'Stopped',
        help: 'Indexing paused — resume to finish',
        cls: 'bg-amber-50 text-amber-700 ring-amber-100',
        dot: 'bg-amber-500',
    },
    superseded: {
        label: 'Older export',
        help: 'Kept for reference',
        cls: 'bg-stone-100 text-stone-600 ring-stone-200',
        dot: 'bg-stone-400',
    },
    skipped_delta: {
        label: 'Delta skipped',
        help: 'Full exports are used',
        cls: 'bg-stone-100 text-stone-600 ring-stone-200',
        dot: 'bg-stone-400',
    },
    drift_blocked: {
        label: 'Needs review',
        help: 'Schema changed',
        cls: 'bg-red-50 text-red-700 ring-red-100',
        dot: 'bg-red-500',
    },
};

function StatusBadge({ status, autoIndexing }) {
    let s = STATUS_STATES[status] || {
        label: status,
        help: '',
        cls: 'bg-stone-100 text-stone-600 ring-stone-200',
        dot: 'bg-stone-400',
    };
    if (status === 'new' && !autoIndexing) {
        s = {
            label: 'Not indexed',
            help: 'Indexing is off',
            cls: 'bg-amber-50 text-amber-700 ring-amber-100',
            dot: 'bg-amber-500',
        };
    }
    return (
        <div className="flex flex-col items-start gap-0.5">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ring-1 whitespace-nowrap ${s.cls}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                {s.label}
            </span>
            {s.help && <span className="text-[10px] text-stone-400 whitespace-nowrap">{s.help}</span>}
        </div>
    );
}

function IndexProgress({ progress, stopped = false }) {
    const total = progress?.total_steps || 0;
    const step = progress?.step || 0;
    const rows = progress?.rows_done || 0;
    const current = progress?.current;
    const pct = total > 0 ? Math.min(100, Math.round((step / total) * 100)) : null;

    const parts = [];
    if (total > 0) parts.push(`Step ${step}/${total}`);
    if (current) parts.push(current.replace(/\.csv$/i, ''));
    if (rows > 0) parts.push(`${rows.toLocaleString()} rows`);
    // Before the zip is opened there are no step counts yet — say so rather than
    // leaving the bar unlabelled (which reads as "nothing is happening").
    const label = parts.length
        ? parts.join(' · ')
        : (stopped ? 'Paused' : 'Preparing — reading export…');

    return (
        <div className="mt-1.5 w-48">
            <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-500 ${stopped ? 'bg-amber-400' : 'bg-violet-500'} ${pct === null && !stopped ? 'w-1/3 animate-pulse' : ''}`}
                    style={pct === null ? (stopped ? { width: '100%' } : undefined) : { width: `${pct}%` }}
                />
            </div>
            <p className="mt-1 text-[10px] text-stone-400 truncate">{label}</p>
        </div>
    );
}

function ActionIconButton({ label, disabled = false, loading = false, onClick, children }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="inline-flex" tabIndex={disabled ? 0 : undefined}>
                    <Button
                        size="icon"
                        variant="outline"
                        aria-label={label}
                        disabled={disabled || loading}
                        className="w-8 h-8"
                        onClick={onClick}>
                        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : children}
                    </Button>
                </span>
            </TooltipTrigger>
            <TooltipContent side="top">{label}</TooltipContent>
        </Tooltip>
    );
}

export default function PitchBookSyncTab() {
    const { getAuthHeader } = useAuth();
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);
    const [savingCfg, setSavingCfg] = useState(false);
    const [runRefresh, setRunRefresh] = useState(0);
    const [filesOpen, setFilesOpen] = useState(false);
    const [mappingOpen, setMappingOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);

    const loadConfig = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/pitchbook/config`, { headers: getAuthHeader() });
            setConfig(res.data);
            setForbidden(false);
        } catch (e) {
            if (e.response?.status === 403) setForbidden(true);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    // No match-review panel: `pitchbook_match_reviews` was only written by the
    // removed CRM sync (#767). Ambiguous matches are resolved in List Management.
    useEffect(() => { loadConfig(); }, [loadConfig]);

    const updateConfig = async (patch) => {
        setSavingCfg(true);
        try {
            const res = await axios.put(`${API}/pitchbook/config`, patch, { headers: getAuthHeader() });
            setConfig(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to update config');
        } finally {
            setSavingCfg(false);
        }
    };

    if (loading) return <div className="py-10 text-center text-stone-400"><Loader2 className="mx-auto w-6 h-6 animate-spin" /></div>;
    if (forbidden) {
        return (
            <div className="py-10 text-center text-stone-500">
                <Database className="mx-auto mb-2 w-8 h-8 text-stone-300" />
                <p className="text-sm">You don't have access to PitchBook Sync. Ask an org admin to add you to the allowlist.</p>
            </div>
        );
    }

    const effective = config.effective || {};
    const autoIndexing = effective.auto_indexing !== false;
    const tickMinutes = Math.max(1, Math.round((config.background_tick_seconds || 300) / 60));

    return (
        <div className="space-y-5">
            {/* Header: title + config entry points (everything else is the data) */}
            <div className="flex flex-wrap gap-3 justify-between items-center">
                <div>
                    <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>PitchBook</h3>
                    <p className="text-xs text-stone-500 mt-0.5">
                        Browse the indexed PitchBook data and preview it against your CRM. Importing happens in List Management.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <Button size="sm" variant="outline" onClick={() => setMappingOpen(true)}>
                        <Columns3 className="w-3.5 h-3.5 mr-1.5" /> Field mapping
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}>
                        <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Sync settings
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setHistoryOpen(true)}>
                        <History className="w-3.5 h-3.5 mr-1.5" /> Run history
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setFilesOpen(true)}>
                        <FileArchive className="w-3.5 h-3.5 mr-1.5" /> Bucket files
                    </Button>
                </div>
            </div>

            {!autoIndexing && (
                <p className="text-xs text-amber-600 flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Automatic indexing is off — new exports will not be picked up until you index files by hand from the bucket files dialog.
                </p>
            )}

            {/* The data browser IS the page */}
            <PitchBookDataBrowser refreshKey={runRefresh} />

            {/* Field mapping dialog */}
            {mappingOpen && (
                <Dialog open onOpenChange={(open) => { if (!open) setMappingOpen(false); }}>
                    <DialogContent className="max-h-[88vh] overflow-y-auto" style={{ width: 1100, maxWidth: '96vw' }}>
                        <DialogHeader>
                            <DialogTitle className="text-base">Field mapping</DialogTitle>
                            <DialogDescription>
                                How PitchBook columns map onto CRM fields, and whether a CRM value that is
                                already set would be kept or overwritten. Drives the preview comparison and the
                                PitchBook tab on company and contact records; List Management has its own mapping step.
                            </DialogDescription>
                        </DialogHeader>
                        <MappingEditor config={config} onSaved={loadConfig} />
                    </DialogContent>
                </Dialog>
            )}

            {/* Sync settings dialog */}
            {settingsOpen && (
                <Dialog open onOpenChange={(open) => { if (!open) setSettingsOpen(false); }}>
                    <DialogContent style={{ width: 640, maxWidth: '94vw' }}>
                        <DialogHeader>
                            <DialogTitle className="text-base">Sync settings</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-5">
                            <div className="flex gap-6 justify-between items-start">
                                <div>
                                    <p className="flex gap-2 items-center text-sm font-medium text-stone-800">
                                        <Database className="w-4 h-4 text-stone-400" /> Automatic indexing
                                    </p>
                                    <p className="mt-1 text-xs text-stone-500">
                                        Keeps a local, searchable copy of your PitchBook export — the CSV records inside
                                        each zip file PitchBook drops into your S3 bucket. Indexing never changes your
                                        CRM records.
                                    </p>
                                    <p className="text-xs text-stone-500 mt-1.5">
                                        <span className="font-medium text-stone-600">When it runs:</span> checks your
                                        bucket for new files every {tickMinutes} minutes, around the clock.
                                    </p>
                                </div>
                                <Switch checked={autoIndexing} disabled={savingCfg}
                                    onCheckedChange={(v) => updateConfig({ auto_indexing: v })} />
                            </div>
                            {!autoIndexing && (
                                <p className="flex gap-1 items-center -mt-2 text-xs text-amber-600">
                                    <AlertTriangle className="w-3.5 h-3.5" /> Indexing is off — new exports must be indexed by hand from the bucket files dialog.
                                </p>
                            )}

                            <div className="border-t border-stone-100" />

                            <div className="p-3 rounded-lg border border-stone-200 bg-stone-50" data-testid="pb-crm-sync-moved">
                                <p className="flex items-center gap-2 text-sm font-medium text-stone-800">
                                    <GitMerge className="w-4 h-4 text-stone-400" /> CRM import
                                </p>
                                <p className="mt-1 text-xs text-stone-500">
                                    The feed only keeps the indexed copy up to date. It never writes your CRM: companies
                                    and people reach the CRM through List Management, where they are reviewed, assigned
                                    and committed by an Admin.
                                </p>
                                <div className="mt-2"><MovedToListManagement compact /></div>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            {/* Run history dialog */}
            {historyOpen && (
                <Dialog open onOpenChange={(open) => { if (!open) setHistoryOpen(false); }}>
                    <DialogContent className="max-h-[88vh] overflow-y-auto" style={{ width: 1000, maxWidth: '96vw' }}>
                        <DialogHeader>
                            <DialogTitle className="flex gap-2 items-center text-base">
                                <History className="w-4 h-4 text-stone-400" /> Run history
                            </DialogTitle>
                            <DialogDescription>Indexing runs, manual imports and automatic syncs, latest first.</DialogDescription>
                        </DialogHeader>
                        <RunHistory refreshKey={runRefresh} onChanged={() => setRunRefresh((k) => k + 1)} />
                    </DialogContent>
                </Dialog>
            )}

            {filesOpen && (
                <BucketFilesDialog
                    autoIndexing={autoIndexing}
                    onClose={() => setFilesOpen(false)}
                    onIndexed={() => setRunRefresh((k) => k + 1)}
                />
            )}
        </div>
    );
}

function BucketFilesDialog({ autoIndexing, onClose, onIndexed }) {
    const { getAuthHeader } = useAuth();
    const [files, setFiles] = useState(null);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [indexingKey, setIndexingKey] = useState(null);
    const [downloadingKey, setDownloadingKey] = useState(null);
    const [stoppingKey, setStoppingKey] = useState(null);

    const load = useCallback(async (newSkip, refresh = false) => {
        if (refresh) setRefreshing(true);
        try {
            const res = await axios.get(`${API}/pitchbook/files`, {
                params: { limit: FILES_PAGE_SIZE, skip: newSkip, refresh },
                headers: getAuthHeader(),
            });
            if (res.data.error) { setError(res.data.error); setFiles([]); setTotal(0); }
            else {
                setFiles(res.data.files || []);
                setTotal(res.data.total || 0);
                setSkip(newSkip);
                setError(null);
            }
        } catch {
            setError('bucket_unreachable'); setFiles([]); setTotal(0);
        } finally {
            setRefreshing(false);
        }
    }, [getAuthHeader]);

    useEffect(() => { load(0); }, [load]);

    // While any file is indexing in the background, poll the list every 5s and
    // toast when a watched file finishes or fails.
    const prevStatuses = useRef({});
    useEffect(() => {
        if (!files) return undefined;
        const prev = prevStatuses.current;
        files.forEach((f) => {
            if (prev[f.key] !== 'indexing') return;
            if (f.status === 'processed') {
                toast.success(`Indexed ${f.name}`);
                onIndexed();
            } else if (f.status === 'failed') {
                toast.error(`Indexing ${f.name} failed${f.error ? ` — ${f.error}` : ''}`);
            } else if (f.status === 'cancelled') {
                toast(`Stopped indexing ${f.name} — resume to finish`);
            } else if (f.status === 'drift_blocked') {
                toast.error(`Indexing ${f.name} blocked: the feed schema changed. Resolve the drift first.`);
            }
        });
        prevStatuses.current = Object.fromEntries(files.map((f) => [f.key, f.status]));
        if (!files.some((f) => f.status === 'indexing')) return undefined;
        const t = setTimeout(() => load(skip), 5000);
        return () => clearTimeout(t);
    }, [files, skip, load, onIndexed]);

    const indexFile = async (f) => {
        setIndexingKey(f.key);
        try {
            await axios.post(`${API}/pitchbook/manual-sync/ingest`,
                { file_key: f.key }, { headers: getAuthHeader() });
            toast.success(`${f.status === 'cancelled' ? 'Resuming' : 'Indexing'} ${f.name} — it runs in the background, you can close this dialog`);
            await load(skip);
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail;
            if (detail?.drift_report) {
                toast.error('Indexing blocked: the feed schema changed. Resolve the drift first.');
            } else if (status === 409) {
                // A run is already in flight for this file — surface it instead of
                // leaving the row frozen on its old status. Refresh so it flips to
                // "Indexing" and the progress bar + polling kick in.
                toast(`${f.name} is already indexing — showing live progress`);
                await load(skip);
            } else {
                toast.error(typeof detail === 'string' ? detail : 'Failed to start indexing');
            }
        } finally {
            setIndexingKey(null);
        }
    };

    const stopIndexing = async (f) => {
        if (!f.run_id) return;
        setStoppingKey(f.key);
        try {
            const res = await axios.post(`${API}/pitchbook/runs/${f.run_id}/cancel`,
                {}, { headers: getAuthHeader() });
            toast(res.data?.forced
                ? `${f.name} cleared — the indexing worker had already stopped`
                : `Stopping ${f.name}… it pauses after the current batch`);
            await load(skip);
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Failed to stop indexing');
        } finally {
            setStoppingKey(null);
        }
    };

    const downloadFile = async (f) => {
        setDownloadingKey(f.key);
        try {
            const res = await axios.get(`${API}/pitchbook/files/download-url`, {
                params: { file_key: f.key },
                headers: getAuthHeader(),
            });
            const a = document.createElement('a');
            a.href = res.data.url;
            a.download = f.name;
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to prepare download');
        } finally {
            setDownloadingKey(null);
        }
    };

    const page = Math.floor(skip / FILES_PAGE_SIZE) + 1;
    const pages = Math.max(1, Math.ceil(total / FILES_PAGE_SIZE));

    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent style={{ width: 900, maxWidth: '94vw' }}>
                <TooltipProvider delayDuration={150}>
                <DialogHeader>
                    <DialogTitle className="flex gap-2 items-center text-base">
                        <FileArchive className="w-4 h-4 text-stone-400" /> Bucket files
                    </DialogTitle>
                    <DialogDescription>
                        PitchBook export zips found in your S3 bucket, latest first.
                        {autoIndexing
                            ? ' New files are indexed automatically — the actions here are only needed for troubleshooting.'
                            : ' Automatic indexing is off, so new files must be indexed from here.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex justify-between items-center">
                    <p className="text-xs text-stone-400">{total} file{total === 1 ? '' : 's'}</p>
                    <button onClick={() => load(skip, true)} disabled={refreshing}
                        className="flex gap-1 items-center text-xs text-stone-400 hover:text-stone-700">
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh from bucket
                    </button>
                </div>

                {error ? (
                    <div className="flex gap-3 items-start p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-amber-800">Unable to reach the PitchBook data source</p>
                            <p className="text-xs text-amber-700 mt-0.5">The connection to your PitchBook bucket could not be established. Please contact your administrator to verify the storage configuration is set up correctly.</p>
                        </div>
                    </div>
                ) : files === null ? (
                    <div className="py-6 text-center text-stone-400"><Loader2 className="mx-auto w-5 h-5 animate-spin" /></div>
                ) : files.length === 0 ? (
                    <p className="py-2 text-sm text-stone-400">No zip files in the bucket.</p>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-stone-200">
                        <table className="w-full text-xs">
                            <thead className="bg-stone-50 text-stone-500">
                                <tr>
                                    <th className="px-4 py-2 font-medium text-left">File</th>
                                    <th className="px-3 py-2 font-medium text-left">Modified</th>
                                    <th className="px-3 py-2 font-medium text-left">Status</th>
                                    <th className="px-4 py-2 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {files.map((f) => (
                                    <tr key={f.key} className="hover:bg-stone-50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex gap-2 items-center font-mono text-stone-700">
                                                    <span>{f.name}</span>
                                                    {f.is_latest && (
                                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700">
                                                            Latest
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-[11px] text-stone-400">{fmtBytes(f.size)}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-stone-500">{f.last_modified ? new Date(f.last_modified).toLocaleString() : '—'}</td>
                                        <td className="px-3 py-2">
                                            <StatusBadge status={f.status} autoIndexing={autoIndexing} />
                                            {(f.status === 'indexing' || (f.status === 'cancelled' && f.progress)) && (
                                                <IndexProgress progress={f.progress} stopped={f.status === 'cancelled'} />
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="flex gap-2 justify-end items-center whitespace-nowrap">
                                                <ActionIconButton
                                                    label="Download zip"
                                                    loading={downloadingKey === f.key}
                                                    onClick={() => downloadFile(f)}>
                                                    <Download className="w-3.5 h-3.5" />
                                                </ActionIconButton>
                                                {f.status === 'indexing' ? (
                                                    <ActionIconButton
                                                        label="Stop indexing"
                                                        loading={stoppingKey === f.key}
                                                        onClick={() => stopIndexing(f)}>
                                                        <Square className="w-3.5 h-3.5 text-rose-600" />
                                                    </ActionIconButton>
                                                ) : f.status !== 'skipped_delta' && (
                                                    <ActionIconButton
                                                        label={f.status === 'cancelled' ? 'Resume indexing' : (f.indexed ? 'Re-index zip' : 'Index zip')}
                                                        loading={indexingKey === f.key}
                                                        onClick={() => indexFile(f)}>
                                                        {f.status === 'cancelled'
                                                            ? <Play className="w-3.5 h-3.5 text-emerald-600" />
                                                            : <DownloadCloud className="w-3.5 h-3.5" />}
                                                    </ActionIconButton>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {total > FILES_PAGE_SIZE && (
                    <div className="flex justify-between items-center pt-1">
                        <p className="text-xs text-stone-400">Page {page} of {pages}</p>
                        <div className="flex gap-2 items-center">
                            <Button size="sm" variant="outline" disabled={skip === 0}
                                onClick={() => load(Math.max(0, skip - FILES_PAGE_SIZE))}>
                                <ChevronLeft className="w-3.5 h-3.5" /> Prev
                            </Button>
                            <Button size="sm" variant="outline" disabled={skip + FILES_PAGE_SIZE >= total}
                                onClick={() => load(skip + FILES_PAGE_SIZE)}>
                                Next <ChevronRight className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>
                )}
                </TooltipProvider>
            </DialogContent>
        </Dialog>
    );
}
